import {
  ArrowRightOutlined,
  CloseOutlined,
  GiftOutlined,
} from "@ant-design/icons";
import { motion, useReducedMotion } from "framer-motion";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

export interface MainOffer {
  id?: number | string;
  name?: string;
  summary?: string;
  details?: string;
  image_1920?: string;
  is_main?: boolean | number | string;
}

interface MainOfferPopupProps {
  offer: MainOffer;
  onClose: () => void;
}

const MainOfferPopup = ({ offer, onClose }: MainOfferPopupProps) => {
  const { t, i18n } = useTranslation();
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  const detailState = {
    id: offer.id,
    name: offer.name,
    summary: offer.summary,
    details: offer.details,
    image: offer.image_1920,
  };

  return (
    <motion.div
      className="ac-main-offer"
      role="presentation"
      dir={i18n.dir()}
      initial={reduceMotion ? false : { opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <motion.section
        className="ac-main-offer__dialog ac--font-expo"
        role="dialog"
        aria-modal="true"
        aria-labelledby="main-offer-title"
        aria-describedby={offer.summary ? "main-offer-summary" : undefined}
        initial={reduceMotion ? false : { opacity: 0, y: 28, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      >
        <button
          type="button"
          className="ac-main-offer__close"
          onClick={onClose}
          aria-label={t("mainOffer.close")}
          autoFocus
        >
          <CloseOutlined />
        </button>

        <Link
          to="/offer-detail"
          state={detailState}
          className="ac-main-offer__link"
          aria-label={t("offersPage.openOfferAria", {
            name: offer.name || t("menu.offers"),
          })}
        >
          <div className="ac-main-offer__media">
            {offer.image_1920 ? (
              <img src={offer.image_1920} alt="" decoding="async" />
            ) : (
              <GiftOutlined
                className="ac-main-offer__placeholder"
                aria-hidden="true"
              />
            )}
            <span className="ac-main-offer__media-shade" aria-hidden="true" />
          </div>

          <div className="ac-main-offer__content">
            <span className="ac-main-offer__eyebrow">
              <GiftOutlined aria-hidden="true" />
              {t("mainOffer.featured")}
            </span>
            <h2 id="main-offer-title">
              {offer.name || t("banners.title2")}
            </h2>
            {offer.summary ? (
              <p id="main-offer-summary">{offer.summary}</p>
            ) : null}
            <span className="ac-main-offer__cta">
              {t("mainOffer.view")}
              <ArrowRightOutlined aria-hidden="true" />
            </span>
          </div>
        </Link>
      </motion.section>
    </motion.div>
  );
};

export default MainOfferPopup;
